import { checkDateIsBetween, getPercentage, mbGbDisplay } from "./helpers"
import {
  ActionBarModel,
  ApiGroup,
  ApiMember,
  ApiNotificationType,
  ApiReceiptModel,
  DataPlanModel,
  GroupModel,
  NotificationType,
  OrganizationType,
  SharedDataPlan,
  SingleDataPlan,
  TerminalApiType,
  TerminalDataModel,
  Member,
  ApiTerminalModel,
  TerminalModel,
  TerminalType,
} from "./models"
import dayjs from "dayjs"
import { Receipt } from "../components/organisms/ReceiptsCard"
import {
  filterMemberNameAtoZ,
  filterMemberNameZtoA,
  filterMemberTitleAtoZ,
  filterMemberTitleZtoA,
  filterSuborgNumberOfGroupsHighToLow,
  filterSuborgNumberOfGroupsLowToHigh,
  filterSuborgNumberOfMembersHighToLow,
  filterSuborgNumberOfMembersLowToHigh,
  filterSuborgNumberOfTerminalsHighToLow,
  filterSuborgNumberOfTerminalsLowToHigh,
  filterSuborgsNameAtoZ,
  filterSuborgsNameZtoA,
  searchActivityLog,
  searchReceipts,
  sortTerminalNameAtoZ,
  sortTerminalNameZtoA,
  sortTerminalsDataUsageHighLow,
  sortTerminalsDataUsageLowHigh,
  sortTerminalsGroupsHighLow,
  sortTerminalsGroupsLowHigh,
  sortTerminalsSerialNumberHighLow,
  sortTerminalsSerialNumberLowHigh,
} from "./filter-helpers"
import { FALLBACK_PICTURE, getActivityMessage, NOTIFICATION_TYPES } from "./design-helpers"
import { Product } from "../components/interfaces/SaleorTypes"

export const formatAllDataPlans = (allDataPlans: Array<SingleDataPlan | SharedDataPlan>, empty?: boolean) => {
  const formattedDataPlans: Array<DataPlanModel> = []

  if (empty) {
    return []
  }

  allDataPlans?.map((dataPlan: SingleDataPlan | SharedDataPlan) => {
    if (dataPlan.__typename === "SingleDataPlanType") {
      const singlePlan: SingleDataPlan = dataPlan as SingleDataPlan

      const percentageDataUsage = getPercentage(singlePlan.totalDataUsage ?? 0, singlePlan.volumeTotalAggregated ?? 0)

      const dataUsage = percentageDataUsage > 100 ? 100 : isNaN(percentageDataUsage) ? 0 : percentageDataUsage

      const startDate = dayjs(singlePlan.start).format("DD MMM YYYY")
      const endDate = dayjs(singlePlan.end).format("DD MMM YYYY")

      const nickname = singlePlan.terminal?.terminal.nickname ?? ""

      const terminalData: TerminalDataModel = singlePlan.terminal && {
        imgSrc: singlePlan.terminal.terminal.imageUrl ?? "",
        id: parseFloat(singlePlan.terminal.id.toString()),
        name: nickname.length > 0 ? nickname : `Terminal ${singlePlan.terminal.terminal.serialNumber}`,
        serialNumber: singlePlan.terminal.terminal.serialNumber ?? "",
        sudoPassword: singlePlan.terminal.terminal.superAdminPassword ?? "n/a",
        modemSerialNumber: singlePlan.terminal.terminal.modemSn ?? "",
        warrantyEndsDate: singlePlan.terminal.terminal.warrantyEndDate
          ? dayjs(singlePlan.terminal.terminal.warrantyEndDate).format("DD MMM YYYY")
          : endDate,
        suborganization: singlePlan.organization?.nameOrganization ?? "",
        dataplan: singlePlan.name ?? "n/a",
        sharedPlan: false,
        apiPlanId: singlePlan.apiPlanId,
        status: singlePlan.terminal?.active ? "active" : "ended",
        dataUsage: {
          used: singlePlan?.totalDataUsage ?? 0,
          total: singlePlan.volumeTotalAggregated ?? 0,
        },
        groups: singlePlan.terminal?.terminal?.groups ?? [],
        apiTerminalId: singlePlan.terminal.id,
        firstDataplanStartDate: startDate,
      }

      const ab: ActionBarModel = {
        validDates: `${startDate} - ${endDate}`,
        currentProgress: dataUsage,
        dataUsage: `${mbGbDisplay(singlePlan.totalDataUsage ?? 0)} / ${mbGbDisplay(
          singlePlan.volumeTotalAggregated ?? 0,
        )}`,
        dataPlanName: singlePlan?.name ?? "n/a",
        suborganisation: singlePlan?.organization?.nameOrganization,
        dataLeft: (singlePlan?.volumeTotalAggregated ?? 0) - (singlePlan?.totalDataUsage ?? 0),
        status: singlePlan?.status,
        canEditName: singlePlan?.canEditName,
      }
      const obj: DataPlanModel = {
        id: singlePlan.apiPlanId?.toString(),
        actionBarProps: ab,
        terminalData: terminalData ? [terminalData] : [],
        name: singlePlan?.name,
        sharedPlan: false,
        dataLastSynced: singlePlan?.terminal.lastSyncedTotalMetrics,
        apiPlanId: singlePlan.apiPlanId,
      }

      formattedDataPlans.push(obj)
    } else {
      const sharedDataPlan: SharedDataPlan = dataPlan as SharedDataPlan

      const percentageDataUsage = getPercentage(
        sharedDataPlan.totalDataUsage ?? 0,
        sharedDataPlan.volumeTotalAggregated ?? 0,
      )

      const dataUsage = percentageDataUsage > 100 ? 100 : isNaN(percentageDataUsage) ? 0 : percentageDataUsage

      const startDate = dayjs(sharedDataPlan.start).format("DD MMM YYYY")
      const endDate = dayjs(sharedDataPlan.end).format("DD MMM YYYY")

      const ab: ActionBarModel = {
        validDates: `${startDate} - ${endDate}`,
        currentProgress: dataUsage,
        dataUsage: `${mbGbDisplay(sharedDataPlan?.totalDataUsage ?? 0)} / ${mbGbDisplay(
          sharedDataPlan?.volumeTotalAggregated ?? 0,
        )}`,
        dataPlanName: sharedDataPlan?.name ?? "n/a",
        suborganisation: sharedDataPlan?.organization?.name,
        dataLeft: (sharedDataPlan?.volumeTotalAggregated ?? 0) - (sharedDataPlan?.totalDataUsage ?? 0),
        status: sharedDataPlan?.status,
        canEditName: sharedDataPlan?.canEditName,
      }
      const obj: DataPlanModel = {
        id: sharedDataPlan.apiPlanId.toString(),
        actionBarProps: ab,
        terminalData: [],
        name: sharedDataPlan.name,
        sharedPlan: true,
        apiPlanId: parseInt(sharedDataPlan.apiPlanId.toString()),
      }

      formattedDataPlans.push(obj)
    }
  })

  return formattedDataPlans
}

export const formatSharedPlanTerminalsToTerminalDataModel = (
  sharedInstances: Array<any>,
  suborg: string = "",
  dataplan: string = "",
  sorting?: string,
  startDate?: string,
) => {
  let terminalData: Array<TerminalDataModel> = sharedInstances?.map(
    (instance: {
      terminal: TerminalApiType
      id: any
      name?: string
      organization: { id: any; name?: string }
      totalDataUsage?: number
      volumeTotalAggregated?: number
      volumeTopupAggregated?: number
      apiPlanId: number
    }) => {
      const totalData = instance.volumeTotalAggregated ?? 0

      const obj: TerminalDataModel = {
        imgSrc: instance.terminal.terminal.imageUrl ?? "",
        id: parseFloat(instance.terminal.id.toString()),
        groups: instance.terminal.terminal.groups ?? [],
        name: instance.terminal.terminal.nickname ?? `Terminal ${instance.terminal.terminal.serialNumber}`,
        serialNumber: instance.terminal.terminal.serialNumber ?? "",
        sudoPassword: instance.terminal.terminal.superAdminPassword ?? "n/a",
        modemSerialNumber: instance.terminal.terminal.modemSn ?? "",
        warrantyEndsDate: dayjs(instance.terminal.terminal.warrantyEndDate).format("DD MMM YYYY") ?? "",
        suborganization: instance?.organization?.name || "",
        dataplan: instance?.name ?? "",
        sharedPlan: true,
        apiPlanId: instance.apiPlanId,
        status: instance.terminal?.active ? "active" : "undefined",
        dataUsage: { used: instance.totalDataUsage ?? 0, total: totalData },
        apiTerminalId: instance.terminal.apiId,
        firstDataplanStartDate: startDate,
      }
      return obj
    },
  )

  if (sorting === "naz") {
    const newList = sortTerminalNameAtoZ(terminalData)
    terminalData = newList
  } else if (sorting === "nza") {
    const newList = sortTerminalNameZtoA(terminalData)
    terminalData = newList
  } else if (sorting === "snhl") {
    const newList = sortTerminalsSerialNumberHighLow(terminalData)
    terminalData = newList
  } else if (sorting === "snlh") {
    const newList = sortTerminalsSerialNumberLowHigh(terminalData)
    terminalData = newList
  } else if (sorting === "duhl") {
    const newList = sortTerminalsDataUsageHighLow(terminalData)
    terminalData = newList
  } else if (sorting === "dulh") {
    const newList = sortTerminalsDataUsageLowHigh(terminalData)
    terminalData = newList
  } else if (sorting === "ghl") {
    const newList = sortTerminalsGroupsHighLow(terminalData)
    terminalData = newList
  } else if (sorting === "glh") {
    const newList = sortTerminalsGroupsLowHigh(terminalData)
    terminalData = newList
  }

  return terminalData
}

export const formatApiTerminalsToTerminalModel = (terminals: Array<TerminalModel>, empty?: boolean) => {
  const formattedTerminals: Array<TerminalDataModel> = []

  if (empty) {
    return []
  }

  terminals?.map((terminal: TerminalModel) => {
    const total = terminal.apiTerminal?.latestDataPlan?.volumeTotalAggregated ?? 0

    const newObj: TerminalDataModel = {
      imgSrc: terminal.imageUrl ?? "",
      name: terminal.nickname ?? "",
      serialNumber: terminal.serialNumber ?? "",
      sudoPassword: terminal.superAdminPassword ?? "n/a",
      modemSerialNumber: terminal.modemSn ?? "",
      warrantyEndsDate: terminal.warrantyEndDate,
      suborganization: terminal.organization.name ?? "",
      status: terminal.apiTerminal?.latestDataPlan?.status,
      dataplan: terminal.apiTerminal?.latestDataPlan?.name?.substring(0, 30),
      dataUsage: {
        used: terminal.apiTerminal?.latestDataPlan?.totalDataUsage ?? 0,
        total: total,
      },
      id: terminal.id,
      modemName: terminal.modem,
      canEditName: terminal.canEditName,
      sharedPlan: terminal.apiTerminal?.latestDataPlan?.__typename !== "SingleDataPlanType",
      apiPlanId: terminal.apiTerminal?.latestDataPlan?.apiPlanId,
      firstDataplanStartDate: terminal.apiTerminal?.latestDataPlan?.start,
      groups: terminal.groups,
      apiTerminalId: terminal.apiTerminal?.id,
    }

    formattedTerminals.push(newObj)
  })

  return formattedTerminals
}

export const formatApiOrgTerminals = (terminals: Array<TerminalType>) => {
  let formattedTerminals: Array<TerminalDataModel> = []

  terminals.map((t: TerminalType) => {
    const formattedTerminal: TerminalDataModel = {
      imgSrc: t.imageUrl ?? "",
      name: t.nickname ?? "",
      serialNumber: t.serialNumber ?? "",
      sudoPassword: t.superAdminPassword ?? "",
      modemSerialNumber: t.modemSn ?? "",
      warrantyEndsDate: t.warrantyEndDate ?? "",
      suborganization: t.organization?.name ?? "",
      orgId: t.organization?.id ?? "error",
      status: "n/a",
      dataplan: t.apiTerminal?.latestDataPlan?.name ?? "",
      dataUsage: { used: 0, total: 0 },
      id: t.id,
      modemName: t.modem ?? "",
      canEditName: false,
      sharedPlan: t.apiTerminal?.latestDataPlan?.__typename !== "SingleDataPlanType",
      apiPlanId: 0,
      firstDataplanStartDate: "n/a",
      groups: t.groups,
      apiTerminalId: t.apiTerminal?.apiId ?? "error",
    }

    formattedTerminals.push(formattedTerminal)
  })

  return formattedTerminals
}

export const getformattedReceipts = (
  apiReceipts: Array<ApiReceiptModel>,
  searchString?: string,
  allTime?: boolean,
  fromDate?: any,
  toDate?: any,
  filter?: Array<string | number>,
) => {
  const getReadableOrderType = (orderType?: string) => {
    switch (orderType) {
      case "direct":
        return "Store Purchase"
      case "topup":
        return "Topup"
      case "rental":
        return "Rental"
      default:
        return "Store Purchase"
    }
  }
  let receipts: Array<Receipt> = []
  const defaultPreview =
    "https://images.ctfassets.net/kzqg847wz5jr/4Q5YrMu68Hu4egZtQihUMh/8a3b698cb693124981c5bc9fa556dde8/logo__1_.png"

  apiReceipts.map((r) => {
    const object: Receipt = {
      id: r.id,
      previewSrc: defaultPreview,
      title: `Order ${r.order ? "#" + r.order.number : ""}`,
      date: r.date,
      bookmarked: r.bookmarked,
      receiptUrl: r.receiptUrl,
      buyer: r.buyerName,
      order: r.order,
      readableOrderType: getReadableOrderType(r.order?.orderType),
    }
    receipts.push(object)
  })

  if (searchString && searchString.length > 0) {
    const searchFilteredRecipes = searchReceipts(receipts, searchString)
    receipts = searchFilteredRecipes
  }
  if (!allTime) {
    const myFrom = fromDate ?? dayjs()
    const myTo = toDate ?? dayjs()
    const list = receipts.filter((x) => checkDateIsBetween(x.date, myFrom, myTo))
    receipts = list
  }

  if (filter) {
    const filteredList = receipts.filter((x) => filter.includes(x.order ? x.order.orderType : "direct"))
    receipts = filteredList
  }

  return receipts
}

export const getActivities = (
  activitiesList: Array<any>,
  searchTerm?: string,
  allTime?: boolean,
  fromDate?: any,
  toDate?: any,
  filter?: Array<string | number>,
) => {
  let activities: Array<NotificationType> = activitiesList

  if (searchTerm) {
    const list = searchActivityLog(activities, searchTerm)
    activities = list
  }
  if (!allTime) {
    const myFrom = fromDate ?? dayjs()
    const myTo = toDate ?? dayjs()
    const list = activities.filter((x) => checkDateIsBetween(x.date, myFrom, myTo))
    activities = list
  }

  if (filter) {
    const filteredList = activities.filter((x) => filter.includes(x.type))
    activities = filteredList
  }

  return activities
}

export const formatApiActivityLog = (list: Array<ApiNotificationType>) => {
  const newList: Array<NotificationType> = []

  list.map((activityItem: ApiNotificationType) => {
    const id: number = activityItem.id
    const read: boolean = activityItem.read
    const date = dayjs(activityItem.dateTime).format("DD MMM YYYY")
    const type = activityItem.action ?? activityItem.userAction ?? NOTIFICATION_TYPES.ERROR
    const message = getActivityMessage(
      type,
      activityItem.user,
      activityItem?.toUser,
      activityItem?.group,
      activityItem?.toTerminalSn,
      activityItem?.toGroupName,
      activityItem?.toOrganizationName,
      activityItem?.toUserEmail,
    )
    const isUserAction = activityItem.userAction ? true : false

    const obj: NotificationType = {
      id,
      read,
      date,
      type,
      message,
      isUserAction,
    }
    newList.push(obj)
  })

  return newList
}

export const formatMembers = (list: Array<ApiMember>, searchTerm?: string, sorting?: string, isAdmin?: boolean) => {
  let memberList: Array<Member> = []

  list.map((m: ApiMember, index: number) => {
    const newMember: Member = {
      member: {
        id: `${m.firstName} ${index}`,
        src: m.profile?.imageUrl ?? FALLBACK_PICTURE,
        role: isAdmin ? "Admin" : "Viewer",
        title: m.title ?? "",
        suborg: "TBA suborg",
        name: `${m.firstName} ${m.lastName}`,
        email: m.email ?? "error",
        organizationId: m.organization?.id ?? "undefined",
      },
    }

    memberList.push(newMember)
  })

  if (searchTerm) {
    const list: Array<Member> = searchActivityLog(memberList, searchTerm)
    memberList = list
  }

  if (sorting) {
    if (sorting === "nza") {
      memberList = filterMemberNameZtoA(memberList)
    } else if (sorting === "naz") {
      memberList = filterMemberNameAtoZ(memberList)
    } else if (sorting === "taz") {
      memberList = filterMemberTitleAtoZ(memberList)
    } else if (sorting === "tza") {
      memberList = filterMemberTitleZtoA(memberList)
    }
  }

  return memberList
}

export const formatAllOrganizationMembers = (list: Array<ApiMember>) => {
  let memberList: Array<Member> = []

  list.map((m: ApiMember) => {
    const newMember: Member = {
      member: {
        id: m.id,
        src: m.profile?.imageUrl ?? FALLBACK_PICTURE,
        role: m.organizationRole?.name ?? "",
        title: m.title ?? "",
        suborg: m.organization?.name ?? "",
        suborgId: m.organization?.id ?? "",
        name: `${m.firstName} ${m.lastName}`,
        email: m.email ?? "error",
        groups: m.groups,
        organizationId: m.organization?.id ?? "",
      },
    }

    memberList.push(newMember)
  })

  return memberList
}

export const formatGroups = (
  list: Array<ApiGroup>,
  searchTerm?: string,
  sorting?: string,
  suborgFilter?: Array<number>,
) => {
  if (suborgFilter?.length === 0) {
    return []
  }

  let groupList: Array<GroupModel> = []

  list.map((group: ApiGroup) => {
    const id = group.id
    const groupName = group.name
    const isAdminGroup = group.isAdminGroup
    const organization = group.organization

    let terminals: Array<{
      src: string
      name: string
      serialNumber: string
      id: string | number
    }> = []
    group.terminals?.map((terminal: TerminalType) => {
      const obj: {
        src: string
        name: string
        serialNumber: string
        id: string | number
      } = {
        src: terminal.imageUrl ?? "",
        name: terminal.nickname ?? "",
        serialNumber: terminal.serialNumber ?? "",
        id: terminal.id,
      }
      terminals.push(obj)
    })

    let members: Array<Member> = []
    group?.roles[0]?.users?.map((m: ApiMember, index: number) => {
      const newMember: Member = {
        member: {
          id: `${m.firstName} ${index}`,
          src: m.profile?.imageUrl ?? FALLBACK_PICTURE,
          role: "Admin",
          title: m.title ?? "",
          suborg: "TBA suborg",
          name: `${m.firstName} ${m.lastName}`,
          email: m.email ?? "error email",
          organizationId: group?.organization?.id,
        },
      }
      members.push(newMember)
    })
    group?.roles[1]?.users?.map((m: ApiMember, index: number) => {
      const newMember: Member = {
        member: {
          id: `${m.firstName} ${index}`,
          src: m.profile?.imageUrl ?? FALLBACK_PICTURE,
          role: "Viewer",
          title: m.title ?? "",
          suborg: "TBA suborg",
          name: `${m.firstName} ${m.lastName}`,
          email: m.email ?? "error email",
          organizationId: group?.organization?.id,
        },
      }
      members.push(newMember)
    })

    const newGroup: GroupModel = {
      id: id.toString(),
      groupName: groupName,
      isAdminGroup: isAdminGroup,
      members: members,
      terminals: terminals,
      organization: organization,
    }

    groupList.push(newGroup)
  })

  return groupList
}

export const getFilteredSuborganizations = (
  suborgs: Array<OrganizationType>,
  searchTerm?: string,
  sorting?: string,
) => {
  let suborgList: Array<OrganizationType> = suborgs

  if (searchTerm) {
    const list: Array<OrganizationType> = searchActivityLog(suborgList, searchTerm)
    suborgList = list
  }

  if (sorting) {
    if (sorting === "nza") {
      suborgList = filterSuborgsNameZtoA(suborgList)
    } else if (sorting === "naz") {
      suborgList = filterSuborgsNameAtoZ(suborgList)
    } else if (sorting === "nmhl") {
      suborgList = filterSuborgNumberOfMembersHighToLow(suborgList)
    } else if (sorting === "nmlh") {
      suborgList = filterSuborgNumberOfMembersLowToHigh(suborgList)
    } else if (sorting === "nthl") {
      suborgList = filterSuborgNumberOfTerminalsHighToLow(suborgList)
    } else if (sorting === "ntlh") {
      suborgList = filterSuborgNumberOfTerminalsLowToHigh(suborgList)
    } else if (sorting === "nghl") {
      suborgList = filterSuborgNumberOfGroupsHighToLow(suborgList)
    } else if (sorting === "nglh") {
      suborgList = filterSuborgNumberOfGroupsLowToHigh(suborgList)
    }
  }

  return suborgList
}

export const getProductSpecificsForStoreCards = (product: Product) => {
  const metadata = product?.metadata
  let desc = ""
  let slideOneText = ""
  let productSheetLink = ""
  let statOneIcon = ""
  let statOneText = ""
  let statTwoIcon = ""
  let statTwoText = ""
  let statThreeIcon = ""
  let statThreeText = ""
  let statFourIcon = ""
  let statFourText = ""
  let statOneSubtext = ""
  let statTwoSubtext = ""
  let statThreeSubtext = ""
  let statFourSubtext = ""
  let tagline = ""
  let specifics: Array<any> = []

  metadata?.map((x) => {
    if (x.key === "description") {
      desc = x.value
    } else if (x.key === "slideOneText") {
      slideOneText = x.value
    } else if (x.key === "productSheetLink") {
      productSheetLink = x.value
    } else if (x.key === "statOneIcon") {
      statOneIcon = x.value
    } else if (x.key === "statOneText") {
      statOneText = x.value
    } else if (x.key === "statTwoIcon") {
      statTwoIcon = x.value
    } else if (x.key === "statTwoText") {
      statTwoText = x.value
    } else if (x.key === "statThreeIcon") {
      statThreeIcon = x.value
    } else if (x.key === "statThreeText") {
      statThreeText = x.value
    } else if (x.key === "statFourIcon") {
      statFourIcon = x.value
    } else if (x.key === "statFourText") {
      statFourText = x.value
    } else if (x.key === "tagline") {
      tagline = x.value
    } else if (x.key === "statOneSubtext") {
      statOneSubtext = x.value
    } else if (x.key === "statTwoSubtext") {
      statTwoSubtext = x.value
    } else if (x.key === "statThreeSubtext") {
      statThreeSubtext = x.value
    } else if (x.key === "statFourSubtext") {
      statFourSubtext = x.value
    } else if (x.key === "specifics") {
      const jsonObject = JSON.parse(x.value)
      specifics = jsonObject
    }
  })

  return {
    specifics: specifics,
    desc: desc,
    slideOneText: slideOneText,
    productSheetLink: productSheetLink,
    statOneIcon: statOneIcon,
    statOneText,
    statTwoIcon,
    statTwoText,
    statThreeIcon,
    statThreeText,
    statFourIcon,
    statFourText,
    tagline,
    statOneSubtext,
    statTwoSubtext,
    statThreeSubtext,
    statFourSubtext,
  }
}
